import type { ThemeObject } from "@circle-react/types/Theme";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useFrame } from "react-frame-component";

import type { ThemeContextProps } from "../ThemeContext";
import { APPEARANCE } from "../themes";
import { updateTheme } from "../updateTheme";

export const useThemeState = (theme?: Partial<ThemeObject>, selector = "") => {
  const {
    default_appearance = APPEARANCE.LIGHT,
    custom_colors = {},
    current_theme = "default",
    is_custom: isCustom = false,
    brand_color = {
      light: "",
      dark: "",
    },
    brand_text_color = {
      light: "",
      dark: "",
    },
  } = theme ?? {};
  const { document } = useFrame();
  const [currentAppearance, setCurrentAppearance] =
    useState(default_appearance);

  useEffect(() => {
    updateTheme({
      theme: {
        current_appearance: currentAppearance,
        custom_colors,
        current_theme,
        is_custom: isCustom,
        brand_color,
        brand_text_color,
      },
      documentElement: document?.documentElement,
      selector,
    });
  }, [
    brand_color,
    brand_text_color,
    currentAppearance,
    current_theme,
    custom_colors,
    document,
    isCustom,
    selector,
  ]);

  const toggleAppearance = useCallback(() => {
    setCurrentAppearance(
      currentAppearance === APPEARANCE.LIGHT
        ? APPEARANCE.DARK
        : APPEARANCE.LIGHT,
    );
  }, [setCurrentAppearance, currentAppearance]);

  const setAppearance = useCallback(
    (appearance: string) => {
      if (appearance === APPEARANCE.LIGHT || appearance === APPEARANCE.DARK) {
        setCurrentAppearance(appearance);
      }
    },
    [setCurrentAppearance],
  );

  const isDarkAppearance = currentAppearance === APPEARANCE.DARK;
  const isLightAppearance = currentAppearance === APPEARANCE.LIGHT;

  return useMemo<ThemeContextProps>(
    () => ({
      brandColor: brand_color,
      brandTextColor: brand_text_color,
      currentAppearance,
      currentTheme: current_theme,
      customColors: custom_colors,
      isCustomTheme: isCustom,
      isDarkAppearance,
      isLightAppearance,
      setAppearance,
      toggleAppearance,
    }),
    [
      brand_color,
      brand_text_color,
      current_theme,
      currentAppearance,
      custom_colors,
      isCustom,
      isDarkAppearance,
      isLightAppearance,
      setAppearance,
      toggleAppearance,
    ],
  );
};
